import { CarrierVisitStatus } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import { TomTable, useDefaultMRTOptions, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef, MRT_TableInstance } from 'material-react-table'
import { IDataExportConfig } from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useMemo } from 'react'
import { VesselVisitTableDto } from '../Stores/VesselVisitViewStore'
import { getLVesselVisitListColumns } from '../tableConfigs/vesselVisitListColumns'
import { VesselVisitTableDetailPanel } from './atoms/VesselVisitTableDetailPanel'
import { VesselVisitMenu } from './molecules/VesselVisitMenu'

interface VesselVisitTableProps {
  data: VesselVisitTableDto[]
  renderTableCustomActions: ({
    table,
  }: {
    table: MRT_TableInstance<VesselVisitTableDto>
  }) => React.ReactNode
}

export const VesselVisitTable = ({ data, renderTableCustomActions }: VesselVisitTableProps) => {
  const { t } = useTranslate()

  const columns = useMemo<MRT_ColumnDef<VesselVisitTableDto>[]>(
    () => getLVesselVisitListColumns(t),
    [t],
  )

  const dataExportConfig: IDataExportConfig<VesselVisitTableDto> = {
    number: { header: t('No', 'No'), type: 'string' },
    identifier: { header: t('vessel', 'Vessel'), type: 'string' },
    vesselType: { header: t('type', 'Type'), type: 'string' },
    vesselLength: { header: t('length', 'Length (m)'), type: 'number' },
    operatorName: { header: t('operator', 'Operator'), type: 'string' },
    agentName: { header: t('agent', 'Agent'), type: 'string' },
    importVoyage: { header: t('impVoy', 'Imp. voy'), type: 'string' },
    exportVoyage: { header: t('expVoy', 'Exp. voy'), type: 'string' },
    portCallId: { header: t('portCallId', 'Port Call ID'), type: 'string' },
    status: { header: t('status', 'Status'), type: 'string' },
    eta: { header: t('eta', 'ETA'), type: 'date' },
    etb: { header: t('etb', 'ETB'), type: 'date' },
    etd: { header: t('etd', 'ETD'), type: 'date' },
    ata: { header: t('ata', 'ATA'), type: 'date' },
    atb: { header: t('atb', 'ATB'), type: 'date' },
    atd: { header: t('atd', 'ATD'), type: 'date' },
    berthNames: { header: t('berth', 'Berth'), type: 'string' },
    moves: { header: t('moves', 'Moves'), type: 'number' },
    cargoType: { header: t('cargoType', 'Cargo type'), type: 'string' },
    discharge: { header: t('discharge', 'Discharge'), type: 'number' },
    load: { header: t('load', 'Load'), type: 'number' },
    restow: { header: t('restow', 'Restow'), type: 'number' },
    dockingSide: { header: t('dockingSide', 'Docking side'), type: 'string' },
    endOfOperations: {
      header: t('endOfOperations', 'End of operations'),
      type: 'date',
    },
    startOfOperations: {
      header: t('startOfOperations', 'Start of operations'),
      type: 'date',
    },
    notes: { header: t('notes', 'Notes'), type: 'string' },
    orderIds: { hide: true },
    id: { hide: true },
  }

  const dataExport = {
    title: t('vesselVisits', 'Vessel Visits'),
    enableDataExport: true,
    dataExportConfig,
  }

  const defaultOptions = useDefaultMRTOptions<VesselVisitTableDto>(undefined, dataExport)

  const { savedPreferences, onPreferencesChange } =
    useTablePreferencesConfig('planning/vessel-visits')

  const table = useTomTable(
    {
      columns,
      data: data,
      getRowId: row => row.id.toString(),

      enableRowSelection: true,
      enableExpanding: true,
      getRowCanExpand: () => true,
      renderDetailPanel: ({ row }) => <VesselVisitTableDetailPanel row={row} />,
      renderTopToolbarCustomActions: renderTableCustomActions,
      initialState: {
        ...defaultOptions.initialState,
        sorting: [
          {
            id: 'eta',
            desc: true,
          },
        ],
        columnPinning: {
          left: ['mrt-row-actions', 'mrt-row-expand', 'mrt-row-select', 'number', 'identifier'],
        },
        columnFilters: [
          {
            id: 'status',
            value: [
              CarrierVisitStatus.Expected,
              CarrierVisitStatus.Arrived,
              CarrierVisitStatus.InOperation,
              CarrierVisitStatus.Departed,
              CarrierVisitStatus.Completed,
            ],
          },
        ],
      },
      renderRowActions: ({ row }) => <VesselVisitMenu visit={row.original} />,
    },
    undefined,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
}
